/**
 * Two chefs working together, read out of the tape.
 *
 * An AI seat leaves two kinds of trace: an `UTTERANCE` when it says something,
 * and a `PLAN` for every goal it picks, with the model's short reason. Shown
 * apart they read like planner internals. Merged, they read like a kitchen:
 * "Red: Meat's chopped, leaving it on the counter → handing off chopped meat".
 *
 * A planner answer reaches the tape as a plan followed by its say on the same
 * tick, so a `PLAN` and an `UTTERANCE` from one seat on one tick are one line.
 * A `say` macro is speech and nothing else, so it becomes a said-only line.
 * `answering`, tokens and latency are for analysts and are never read here.
 *
 * Like `utterances.ts`, these are pure functions over every frame rather than a
 * fold, so a seek that restarts the feed from a keyframe forgets nothing.
 */

import type { EventFrame, Macro, PlayerId, SessionLogHeader } from '@overcooked/shared'
import { seatIdentity } from '../render/theme'
import { collectUtterances } from './utterances'

export interface ConversationLine {
  tick: number
  /** 1-based seat, which is what `seatIdentity` turns into Red / Blue. */
  seat: number
  said?: string
  /** The chosen macro in plain words: "chopping onion". */
  doing?: string
  /** The model's own reason, as written. */
  why?: string
}

/** `chopped_meat` → `chopped meat`. Ids are data, so this is all the dressing they get. */
const words = (id: string): string => id.replace(/[_-]+/g, ' ').trim()

/** `pan-0-6-1` → `pan`: the kind of vessel, without the cell it was made on. */
const vessel = (containerId: string): string => words(containerId.split('-')[0] || 'pan')

/** What a chef holds: an ingredient by name, or `plate-0-8-1` as "a plate". */
const item = (id: string): string => (/^plate-/.test(id) ? 'a plate' : words(id))

/**
 * A macro as a viewer would say it.
 *
 * Written for people rather than reused from the agents' `describeMacro`, which
 * speaks to the model in container and cell ids. Chefs are named by colour.
 */
export function macroInWords(macro: Macro, nameOf: (playerId: PlayerId) => string): string {
  switch (macro.verb) {
    case 'fetch':
      return `fetching ${item(macro.ingredient)}`
    case 'chop':
      return `chopping ${words(macro.ingredient)}`
    case 'cook':
      return `cooking ${words(macro.ingredient)} in the ${vessel(macro.container)}`
    case 'carry':
      return `moving the ${vessel(macro.container)}`
    case 'plate':
      return `plating from the ${vessel(macro.from)}`
    case 'serve':
      return 'serving an order'
    case 'wash':
      return 'washing a plate'
    case 'bin':
      return 'throwing away what they hold'
    case 'extinguish':
      return 'putting out a fire'
    case 'wait':
      return 'waiting'
    case 'say':
      return 'talking'
    case 'handoff':
      return `handing off ${item(macro.item)}`
    case 'yield':
      return `stepping aside for ${nameOf(macro.to)}`
  }
}

/**
 * Who sits where.
 *
 * Participants (v3), then the roster (v2). A file with no header at all gets
 * seats in the order its speakers first appear, which is the best a v1 log can
 * say and still keeps two chefs apart.
 */
function seatFinder(header: SessionLogHeader | null): (playerId: PlayerId) => number {
  const known = new Map<PlayerId, number>()
  for (const p of header?.players ?? []) known.set(p.id, p.seat)
  for (const p of header?.participants ?? []) known.set(p.playerId, p.seat)
  return (playerId) => {
    let seat = known.get(playerId)
    if (seat === undefined) {
      seat = Math.max(0, ...known.values()) + 1
      known.set(playerId, seat)
    }
    return seat
  }
}

/** Every line of the conversation, in tick order. */
export function collectConversation(
  frames: EventFrame[],
  header: SessionLogHeader | null,
): ConversationLine[] {
  const seatOf = seatFinder(header)
  const nameOf = (playerId: PlayerId): string => seatIdentity(seatOf(playerId)).label

  // Plans before speech on the same tick, which is the order a seat sends them.
  type Item =
    | { tick: number; order: 0; playerId: PlayerId; macro: Macro; reason?: string }
    | { tick: number; order: 1; playerId: PlayerId; text: string }
  const items: Item[] = []
  for (const frame of frames) {
    for (const event of frame.events) {
      if (event.type !== 'PLAN') continue
      items.push({ tick: event.tick, order: 0, playerId: event.playerId, macro: event.macro, reason: event.reason })
    }
  }
  for (const u of collectUtterances(frames)) {
    items.push({ tick: u.tick, order: 1, playerId: u.playerId, text: u.text })
  }
  items.sort((a, b) => a.tick - b.tick || a.order - b.order)

  const lines: ConversationLine[] = []
  // The most recent line per seat, which is the only one a same-tick event can join.
  const latest = new Map<number, ConversationLine>()
  for (const item of items) {
    const seat = seatOf(item.playerId)
    const open = latest.get(seat)
    const sameMoment = open !== undefined && open.tick === item.tick

    if (item.order === 1) {
      if (sameMoment && open.said === item.text) continue // the say macro's own utterance
      if (sameMoment && open.said === undefined) {
        open.said = item.text
        continue
      }
    } else if (item.macro.verb === 'say') {
      if (sameMoment && open.said === undefined) {
        open.said = item.macro.text
        continue
      }
    } else if (sameMoment && open.doing === undefined) {
      open.doing = macroInWords(item.macro, nameOf)
      if (item.reason) open.why = item.reason
      continue
    }

    const line: ConversationLine =
      item.order === 1
        ? { tick: item.tick, seat, said: item.text }
        : item.macro.verb === 'say'
          ? { tick: item.tick, seat, said: item.macro.text }
          : {
              tick: item.tick,
              seat,
              doing: macroInWords(item.macro, nameOf),
              ...(item.reason ? { why: item.reason } : {}),
            }
    lines.push(line)
    latest.set(seat, line)
  }
  return lines
}

/**
 * The conversation as it stood at `tick`, newest `limit` lines kept.
 *
 * Inclusive of `tick`, and filtered rather than accumulated, so dragging the
 * scrub bar back takes the conversation back with it.
 */
export function conversationUpTo(
  lines: ConversationLine[],
  tick: number,
  limit = Number.POSITIVE_INFINITY,
): ConversationLine[] {
  const heard = lines.filter((line) => line.tick <= tick)
  return heard.length > limit ? heard.slice(heard.length - limit) : heard
}
